import React, { useState, useContext } from 'react';
import { Translations, TranslationsContext } from '../components/translate';
import { createRoot } from 'react-dom/client';
import { StringField } from './fields';
import Select from './select';
import Switch from './switch';
import { isArray } from 'lodash';

interface Field {
  id: string;
  name: string;
  label: string;
  short_name: string;
  type: string;
  description: string;
  value: string | boolean | null;
  required: boolean;
  disabled: boolean;
  choices?: [string, string | [string, string][]][];

  extra_attrs: Record<string, string>;
  errors: string[];
}

interface FieldListProps {
  data: Field[][] | Field[];
  name: string;
  label: string;
}

const FormField: React.FC<{
  field: Field;
  fieldId: string;
  hideLabel?: boolean;
  onChange: (value: string | boolean) => void;
}> = ({ field, fieldId, hideLabel, onChange }) => {
  const { type, value, required, disabled, extra_attrs, choices, errors } = field;

  if (type === 'SelectField') {
    return (
      <Select
        name={fieldId}
        label={field.label}
        options={choices ?? []}
        selectedValue={value as string}
        isErrorVisible={errors.length > 0}
        required={required}
        errorMessage={errors.join(', ')}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (type === 'RadioField') {
    return (
      <fieldset>
        <legend>{field.label}</legend>
        {choices?.map((choice, index) => (
          <label key={index}>
            <input
              type="radio"
              name={fieldId}
              value={choice[0]}
              checked={value === choice[0]}
              required={required}
              disabled={disabled}
              onChange={() => onChange(choice[0])}
            />
            {choice[1]}
          </label>
        ))}
      </fieldset>
    );
  }

  if (type === 'BooleanField') {
    return (
      <Switch
        label={field.label}
        isActive={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.checked)
        }
        inputProps={{
          name: fieldId,
        }}
        size="small"
        isErrorVisible={errors.length > 0}
        errorMessage={errors.join(', ')}
      />
    );
  }

  // TODO: There are more field types to handle. We can add them here as needed.

  return (
    <StringField
      label={!hideLabel && field.label}
      inputProps={{
        id: fieldId,
        name: fieldId,
        value: value as string,
        required,
        disabled,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
        ...extra_attrs,
      }}
      errors={errors}
      isErrorVisible={errors.length > 0}
    />
  );
};

const FieldList: React.FC<FieldListProps> = ({ name, data, label }) => {
  const i18n = useContext(TranslationsContext);
  const [fieldData, setFieldData] = useState<Field[][] | Field[]>(data);
  const isGrouped = isArray(fieldData[0]);

  /**
   * Add a new field group or field.
   */
  const addField = () => {
    if (fieldData.length === 0) return;
    if (isGrouped) {
      const newGroup = (fieldData[0] as Field[]).map((field) => ({
        ...field,
        value: field.type === 'BooleanField' ? false : '',
        errors: [],
      }));
      setFieldData((prev) => [...prev, newGroup] as Field[][]);
    } else {
      const newField = {
        ...(fieldData[0] as Field),
        value: (fieldData[0] as Field).type === 'BooleanField' ? false : '',
        errors: [],
      };
      setFieldData((prev) => [...prev, newField] as Field[]);
    }
  };

  /**
   * Remove a field group or field if there is more than one.
   *
   * @param indexToRemove The index of the field group to remove.
   */
  const removeField = (indexToRemove: number) => {
    if (fieldData.length <= 1) return;

    const newData = [...fieldData] as Field[][] | Field[];
    newData.splice(indexToRemove, 1);

    setFieldData(newData);
  };

  /**
   * Handle field change.
   *
   * @param listIndex The index of the field group.
   * @param itemIndex The index of the field in the group.
   * @param value The new value of the field.
   */
  const handleFieldChange = (
    listIndex: number,
    itemIndex: number,
    value: string | boolean
  ) => {
    if (fieldData.length === 0) return;

    if (isGrouped) {
      const newData = [...fieldData] as Field[][];
      newData[listIndex][itemIndex] = {
        ...newData[listIndex][itemIndex],
        value,
      };
      setFieldData(newData);
    } else {
      const newData = [...fieldData] as Field[];
      newData[listIndex] = {
        ...newData[listIndex],
        value,
      };
      setFieldData(newData);
    }
  };

  const renderFields = () =>
    isGrouped ? (
      <div>
        {fieldData.map((fieldGroup, listIndex) => (
          <fieldset
            className="grid-x padding-bottom-large grid-padding-x-small
              grid-padding-y-small"
            key={listIndex}
          >
            {label && (
              <div className="cell">
                <h3 className="h4">{label}</h3>
              </div>
            )}
            {(fieldGroup as Field[]).map((field, itemIndex) => {
              return (
                <div className="cell" key={itemIndex}>
                  <FormField
                    field={field}
                    fieldId={`${name}-${listIndex}-${field.short_name}`}
                    onChange={(value) => handleFieldChange(listIndex, itemIndex, value)}
                  />
                  {field.description && (
                    <p className="help-text">{field.description}</p>
                  )}
                </div>
              );
            })}
            {fieldData.length > 1 && (
              <div className="cell shrink">
                <button
                  className="button small alert"
                  type="button"
                  onClick={() => removeField(listIndex)}
                >
                  {i18n.gettext('Remove')}
                </button>
              </div>
            )}
          </fieldset>
        ))}
      </div>
    ) : (
      <div>
        {label && (
          <div className="cell padding-bottom">
            <h3 className="h4">{label}</h3>
          </div>
        )}
        {(fieldData as Field[]).map((field, listIndex) => (
          <fieldset
            className="grid-x padding-bottom-large grid-padding-x-small
              grid-padding-y-small"
            key={listIndex}
          >
            <div className="cell auto" key={listIndex}>
              <FormField
                field={field}
                fieldId={`${name}-${listIndex}`}
                hideLabel
                onChange={(value) => handleFieldChange(listIndex, -1, value)}
              />
              {field.description && <p className="help-text">{field.description}</p>}
            </div>
            {fieldData.length > 1 && (
              <div className="cell shrink">
                <button
                  className="button small alert"
                  type="button"
                  onClick={() => removeField(listIndex)}
                  tabIndex={-1}
                >
                  {i18n.gettext('Remove')}
                </button>
              </div>
            )}
          </fieldset>
        ))}
      </div>
    );

  return (
    <div>
      {renderFields()}
      <div className="padding-top">
        <button type="button" className="button secondary small" onClick={addField}>
          {i18n.gettext('Add')}
        </button>
      </div>
    </div>
  );
};

export const renderFieldList = (container: HTMLElement) => {
  const root = createRoot(container);
  root.render(
    <Translations>
      <FieldList
        name={container.dataset.fieldName ?? ''}
        label={container.dataset.fieldLabel ?? ''}
        data={JSON.parse(container.dataset.fieldData ?? '[]') as Field[][] | Field[]}
      />
    </Translations>
  );
};

export default FieldList;
